import { useMemo } from 'react';
import _get from 'lodash/get';
import { User, useWhoamiQuery } from 'graphql-common';
import { WatchQueryFetchPolicy } from '@apollo/client';

type Args = {
  fetchPolicy?: WatchQueryFetchPolicy;
  includeFullData?: boolean;
};

export default function useWhoamiQueryHook(args?: Args) {
  const { fetchPolicy, includeFullData = true } = args || {};
  const { data, previousData, error, refetch, loading, networkStatus } =
    useWhoamiQuery({
      variables: { includeFullData },
      fetchPolicy: fetchPolicy || 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  // A network error (e.g. the tab-focus refetch going out while offline)
  // clears Apollo's `data` to `undefined` even though the previous fetch
  // already succeeded — Apollo only keeps that prior payload on
  // `previousData`. Falling back to it here keeps `data` — and everything
  // ProtectedRoute derives from it, like `hasCachedSession` — pointing at
  // the last known session instead of looking like the user was never
  // authenticated. See DAT-1534.
  const effectiveData = data ?? previousData;

  // Memoized so this object stays reference-stable across renders that don't
  // touch whoami — callers sharing this via React Context (see
  // useWhoamiContext) would otherwise see a "changed" value, and re-render,
  // on every unrelated render of the component calling this hook.
  // Every `currentUser*` scalar is derived from `data` and so can't change
  // independently of it; deriving them inside the factory keeps the dep list
  // down to the query result's own moving parts.
  return useMemo(() => {
    const currentUser = _get(effectiveData, 'whoami.resource') as User;
    return {
      currentUser,
      currentUserDeactivatedAt: currentUser?.deactivatedAt,
      currentUserEmail: currentUser?.email,
      currentUserId: currentUser?.id,
      currentUserLang: currentUser?.lang,
      currentUserPasswordUpdatedAt: currentUser?.passwordUpdatedAt,
      currentUserUnconfirmedEmail: currentUser?.unconfirmedEmail,
      currentUserUsername: currentUser?.username,
      data: effectiveData,
      error,
      firstLoading: loading && effectiveData === undefined,
      loading,
      networkStatus,
      refetch,
    };
  }, [effectiveData, error, loading, networkStatus, refetch]);
}
